import React, { FC } from 'react';
import ModalScreen from 'components/Modals/ModalScreen';
import { Field, useField, FieldArray, useFormikContext } from 'formik';
import { normalizeTimings, Timings } from 'model/schedule';

import {
  Container,
  Actions,
  Switch,
  RowBorder,
  Button,
  WrapperActions,
  Select,
  Row,
  AddButton,
  ActionButton,
  DateWrapper,
  Label,
  RightSelect,
  LeftSelect,
} from './styles';
import { Props } from './types';

const ModalUpdate: FC<Props> = ({ title, selectedDay, setShowModal }) => {
  const [{ value }, { error }, { setValue }] = useField(
    `hoursSettings.${title}`,
  );
  const { isValid } = useFormikContext();
  const restorePreviousValue = () => setValue(selectedDay);

  const closeModal = () =>
    setShowModal &&
    setShowModal({ title: '', open: false, selectedDay: undefined });

  const handleClose = () => {
    restorePreviousValue();
    closeModal();
  };

  const handleSave = () => {
    if (value.timings) {
      const tempValue = {
        ...value,
        timings: value.timings.map(normalizeTimings),
      };
      setValue(tempValue);
    }
    closeModal();
  };

  if (!setShowModal) {
    return null;
  }

  return (
    <ModalScreen
      open
      title={title.charAt(0).toUpperCase() + title.slice(1)}
      showCloseButtonHeader={false}
      handleClose={handleClose}
    >
      <Container>
        <RowBorder>
          <span>{value.open ? 'Open' : 'Closed'}</span>
          <Field
            name={`hoursSettings.${title}.open`}
            as={Switch}
            type="checkbox"
          />
        </RowBorder>
        <div>
          {/* @ts-ignore */}
          <FieldArray name={`hoursSettings.${title}.timings`}>
            {(arrayHelpers) => (
              <div>
                {value.timings &&
                  value.timings.map((timing: Timings, index: number) => (
                    <div key={title + index.toString()}>
                      <Row>
                        <DateWrapper>
                          <Label>From</Label>
                          <Field
                            as={LeftSelect}
                            name={`hoursSettings.${title}.timings[${index}].timeStart.hours`}
                            disabled={!value.open}
                            error={(error || [])[index]}
                          >
                            <option>01</option>
                            <option>02</option>
                            <option>03</option>
                            <option>04</option>
                            <option>05</option>
                            <option>06</option>
                            <option>07</option>
                            <option>08</option>
                            <option>09</option>
                            <option>10</option>
                            <option>11</option>
                            <option>12</option>
                          </Field>
                          <Field
                            as={Select}
                            name={`hoursSettings.${title}.timings[${index}].timeStart.minutes`}
                            disabled={!value.open}
                            error={(error || [])[index]}
                          >
                            <option>00</option>
                            <option>05</option>
                            <option>10</option>
                            <option>15</option>
                            <option>20</option>
                            <option>25</option>
                            <option>30</option>
                            <option>35</option>
                            <option>40</option>
                            <option>45</option>
                            <option>50</option>
                            <option>55</option>
                          </Field>
                          <Field
                            as={RightSelect}
                            disabled={!value.open}
                            name={`hoursSettings.${title}.timings[${index}].timeStart.timeZone`}
                            error={(error || [])[index]}
                          >
                            <option>AM</option>
                            <option>PM</option>
                          </Field>
                        </DateWrapper>
                        <DateWrapper>
                          <Label>To</Label>
                          <Field
                            as={LeftSelect}
                            name={`hoursSettings.${title}.timings[${index}].timeEnd.hours`}
                            disabled={!value.open}
                            error={(error || [])[index]}
                          >
                            <option>01</option>
                            <option>02</option>
                            <option>03</option>
                            <option>04</option>
                            <option>05</option>
                            <option>06</option>
                            <option>07</option>
                            <option>08</option>
                            <option>09</option>
                            <option>10</option>
                            <option>11</option>
                            <option>12</option>
                          </Field>
                          <Field
                            as={Select}
                            name={`hoursSettings.${title}.timings[${index}].timeEnd.minutes`}
                            disabled={!value.open}
                            error={(error || [])[index]}
                          >
                            <option>00</option>
                            <option>05</option>
                            <option>10</option>
                            <option>15</option>
                            <option>20</option>
                            <option>25</option>
                            <option>30</option>
                            <option>35</option>
                            <option>40</option>
                            <option>45</option>
                            <option>50</option>
                            <option>55</option>
                          </Field>
                          <Field
                            as={RightSelect}
                            disabled={!value.open}
                            name={`hoursSettings.${title}.timings[${index}].timeEnd.timeZone`}
                            error={(error || [])[index]}
                          >
                            <option>AM</option>
                            <option>PM</option>
                          </Field>
                          <div>
                            {value.timings.length > 1 && index !== 0 && (
                              <ActionButton
                                type="button"
                                variant="delete"
                                onClick={() => arrayHelpers.remove(index)}
                                disabled={!value.open}
                              />
                            )}
                          </div>
                        </DateWrapper>
                      </Row>

                      {value.timings.length === 1 && (
                        <AddButton
                          type="button"
                          onClick={() =>
                            arrayHelpers.push({
                              timeStart: {
                                hours: value.timings[0].timeEnd.hours,
                                minutes: value.timings[0].timeEnd.minutes,
                                timeZone: value.timings[0].timeEnd.timeZone,
                              },
                              timeEnd: {
                                hours: value.timings[0].timeEnd.hours,
                                minutes: value.timings[0].timeEnd.minutes,
                                timeZone: value.timings[0].timeEnd.timeZone,
                              },
                            })
                          }
                          disabled={!value.open}
                        >
                          Add hour range
                        </AddButton>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </FieldArray>
        </div>
      </Container>
      <Actions>
        <WrapperActions>
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            color="secondary"
            type="button"
            onClick={handleSave}
            disabled={!isValid}
          >
            Save
          </Button>
        </WrapperActions>
      </Actions>
    </ModalScreen>
  );
};

export default ModalUpdate;

// TODO: TFR-29 add story to component
